import React from 'react';
import { motion } from 'framer-motion';
import {
  Clock,
  TrendingUp,
  TrendingDown,
  Target,
  Trophy,
  XCircle,
  DollarSign,
  Activity
} from 'lucide-react';

const RecentActivityCard = ({ transactions = [] }) => {
  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Hace unos minutos';
    } else if (diffInHours < 24) {
      return `Hace ${diffInHours} hora${diffInHours > 1 ? 's' : ''}`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
    }
  };

  const getTransactionIcon = (type, result) => {
    switch (type) {
      case 'bet':
        return result === 'win' ? Trophy : result === 'loss' ? XCircle : Target;
      case 'deposit':
        return TrendingUp;
      case 'withdrawal':
        return TrendingDown;
      default:
        return Activity;
    }
  };

  const getTransactionColor = (type, result) => {
    switch (type) {
      case 'bet':
        return result === 'win' ? 'success' : result === 'loss' ? 'danger' : 'warning';
      case 'deposit':
        return 'success';
      case 'withdrawal':
        return 'primary';
      default:
        return 'gray';
    }
  };

  const getTransactionDescription = (transaction) => {
    const { type, result, step_number, event_name } = transaction;
    
    switch (type) {
      case 'bet':
        if (result === 'win') {
          return `Empate ganado en ${event_name} - Paso ${step_number}`;
        } else if (result === 'loss') {
          return `Apuesta perdida en ${event_name} - Paso ${step_number}`;
        } else {
          return `Apuesta realizada en ${event_name} - Paso ${step_number}`;
        }
      case 'deposit':
        return 'Depósito realizado';
      case 'withdrawal':
        return 'Retiro realizado';
      default:
        return 'Transacción';
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 rounded-xl bg-gray-100 dark:bg-gray-700">
            <Clock className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              📈 Actividad Reciente
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tus últimas transacciones y movimientos
            </p>
          </div>
        </div>

        <div className="text-center py-12">
          <Activity className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Sin actividad reciente
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Cuando realices apuestas o transacciones aparecerán aquí
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 rounded-xl bg-primary-100 dark:bg-primary-900/30">
          <Clock className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            📈 Actividad Reciente
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Tus últimas {transactions.length} transacciones
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {transactions.slice(0, 10).map((transaction, index) => {
          const Icon = getTransactionIcon(transaction.type, transaction.result);
          const color = getTransactionColor(transaction.type, transaction.result);
          const isPositive = transaction.type === 'deposit' || transaction.result === 'win';
          
          return (
            <motion.div
              key={transaction.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center p-4 bg-gray-50 dark:bg-dark-800 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            >
              <div className={`p-3 rounded-xl mr-4 bg-${color}-100 dark:bg-${color}-900/30`}>
                <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {getTransactionDescription(transaction)}
                  </h4>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <span className={`text-sm font-bold ${
                      isPositive 
                        ? 'text-success-600 dark:text-success-400' 
                        : 'text-danger-600 dark:text-danger-400'
                    }`}>
                      {isPositive ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>{formatDate(transaction.created_at)}</span>
                  
                  {transaction.type === 'bet' && (
                    <div className="flex items-center space-x-3">
                      <span>Paso {transaction.step_number}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        transaction.result === 'win' 
                          ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                          : transaction.result === 'loss'
                          ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
                          : 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300'
                      }`}>
                        {transaction.result === 'win' ? 'Ganado' : 
                         transaction.result === 'loss' ? 'Perdido' : 'Pendiente'}
                      </span>
                    </div>
                  )}
                </div>

                {transaction.notes && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    {transaction.notes}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-success-100 dark:bg-success-900/30 rounded-lg">
                <TrendingUp className="w-4 h-4 text-success-600 dark:text-success-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {transactions.filter(t => t.type === 'deposit' || t.result === 'win').length}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Ingresos</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
                <Target className="w-4 h-4 text-warning-600 dark:text-warning-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {transactions.filter(t => t.type === 'bet').length}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Apuestas</p>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <DollarSign className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(
                transactions.reduce((sum, t) => {
                  if (t.type === 'deposit' || t.result === 'win') {
                    return sum + Math.abs(t.amount);
                  } else if (t.type === 'withdrawal' || t.result === 'loss') {
                    return sum - Math.abs(t.amount);
                  }
                  return sum;
                }, 0)
              )}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Balance Neto</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecentActivityCard;